import { envConfig } from '#constants/envConfig';

/**
 * Куда вернуть человека после входа соцсетью в нативной оболочке.
 *
 * В вебе возврат бесплатный: `useOAuth` кладёт текущий адрес в `returnUrl`, и провайдер приводит
 * человека ровно туда, откуда он ушёл, — вместе с `?invite=<код>`. В приложении так нельзя:
 * OAuth уходит в системный браузер, а обратно приходит диплинком
 * `gg.teamplay.twa://oauth-callback?token=…`, в котором исходного адреса нет и быть не может.
 *
 * До этого модуля адрес просто терялся: колбэк уводил на дашборд (или на онбординг у новичка).
 * Для приглашения по ссылке это обрывало ровно тот сценарий, ради которого фича сделана —
 * человек открывал ссылку в приложении, входил через Discord и оказывался на дашборде, а место
 * в сборе оставалось незанятым. Лендинг при этом обещает обратное: «место подтвердите сразу
 * после входа».
 *
 * Поэтому адрес запоминаем перед уходом в браузер и читаем один раз при возврате. Хранилище —
 * `localStorage`: переживает Custom Tab и выгрузку приложения, пока человек в браузере (на
 * Android это не редкость: sessionStorage в новом процессе пуст, и приглашение терялось). От
 * «вернуть на вчерашнюю страницу» защищает срок жизни записи, а не тип хранилища.
 */
const RETURN_PATH_KEY = 'tp:native-oauth-return';

/** Сколько живёт запомненный адрес: вход соцсетью занимает минуту, но сеть бывает медленной. */
const MAX_AGE_MS = 15 * 60 * 1000;

type StoredReturn = { path: string; savedAt: number };

const storage = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    // Приватный режим и запрет данных сайта: хранилище бросает на самом обращении.
    return null;
  }
};

/**
 * Внутренний путь, и только он. Абсолютный адрес из хранилища — это готовый open-redirect,
 * поэтому наружу отдаём лишь то, что начинается с одного слэша.
 */
const isSafeInternalPath = (path: string) => path.startsWith('/') && !path.startsWith('//');

export const rememberNativeOAuthReturn = (path: string) => {
  if (!envConfig.IS_NATIVE || !isSafeInternalPath(path)) {
    return;
  }

  const store = storage();
  if (!store) {
    return;
  }

  try {
    const payload: StoredReturn = { path, savedAt: Date.now() };
    store.setItem(RETURN_PATH_KEY, JSON.stringify(payload));
  } catch {
    // Переполненное хранилище — не повод ронять вход: человек просто вернётся на дашборд.
  }
};

/**
 * Забрать запомненный адрес. Читается один раз: второй вход подряд не должен уводить туда,
 * куда человек шёл в прошлый.
 */
export const takeNativeOAuthReturn = (): string | null => {
  const store = storage();
  if (!store) {
    return null;
  }

  let raw: string | null = null;
  try {
    raw = store.getItem(RETURN_PATH_KEY);
    store.removeItem(RETURN_PATH_KEY);
  } catch {
    return null;
  }

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as StoredReturn;
    if (!parsed?.path || !isSafeInternalPath(parsed.path)) {
      return null;
    }
    if (!parsed.savedAt || Date.now() - parsed.savedAt > MAX_AGE_MS) {
      return null;
    }
    return parsed.path;
  } catch {
    return null;
  }
};
